import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DialogContextProps, DialogContextProviderProps } from './DialogContextProvider.types';
import type { DefaultDialogOptions } from './dialogs/DefaultDialog/DefaultDialog.types';

import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { setUserScopedDialogStorage, useGlobalDialogsStore } from 'stores/global-dialogs/global-dialogs.store';
import { DialogContext } from './DialogContext';
import { CampaignPhoneReachOutDialog } from './dialogs/CampaignPhoneReachOutDialog/CampaignPhoneReachOutDialog';
import { CandidateOutreachDialog } from './dialogs/CandidateOutreachDialog/CandidateOutreachDialog';
import { DefaultDialog } from './dialogs/DefaultDialog/DefaultDialog';
import { ShouldConnectEmailDialog } from './dialogs/ShouldConnectEmailDialog/ShouldConnectEmailDialog';
import { CandidateFeedbackModal } from './modals/CandidateFeedbackModal/CandidateFeedbackModal';
import { DiscardChangesModal } from './modals/DiscardChangesModal/DiscardChangesModal';
import { EnrichVacancyModal } from './modals/EnrichVacancyModal/EnrichVacancyModal';
import { NylasCalendarConnectionModal } from './modals/NylasCalendarConnectionModal/NylasCalendarConnectionModal';
import { ReachoutInterviewModal } from './modals/ReachoutInterviewModal';
import { RejectCandidateModal } from './modals/RejectCandidateModal/RejectCandidateModal';
import { RjCommBulkModal } from './modals/RjCommBulkModal/RjCommBulkModal';
import { SelectAtsVacancyForCampaignModal } from './modals/SelectAtsVacancyForCampaignModal/SelectAtsVacancyForCampaignModal';

export const DialogContextProvider = ({ children }: DialogContextProviderProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [options, setOptions] = useState<DefaultDialogOptions | null>(null);
  const mousePositionRef = useRef<{ x: number; y: number } | null>(null);

  const activeDialogs = useGlobalDialogsStore((state) => state.activeDialogs);
  const { data: currentUser } = useCurrentUserQuery();

  useEffect(() => {
    setUserScopedDialogStorage(currentUser?.id);
  }, [currentUser?.id]);

  const isSomeDialogOpen = isVisible || activeDialogs.length > 0;

  const getMousePosition = useCallback(() => {
    return mousePositionRef.current;
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      mousePositionRef.current = { x: event.clientX, y: event.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const openDialog = useCallback((options: DefaultDialogOptions) => {
    setIsVisible(true);
    setOptions(options);
  }, []);

  const closeDialog = useCallback(() => {
    setIsVisible(false);
    setOptions(null);
  }, []);

  const value: DialogContextProps = useMemo(
    () => ({
      isDialogOpen: isSomeDialogOpen,
      activeDialogs,

      openDialog,
      closeDialog,
      getMousePosition,
    }),
    [isSomeDialogOpen, activeDialogs, openDialog, closeDialog, getMousePosition],
  );

  const opts = options !== null ? { ...options, onClose: () => [options?.onClose?.(), closeDialog()] } : null;

  return (
    <DialogContext.Provider value={value}>
      {children}

      {/* Default dialog */}
      <DefaultDialog isVisible={isVisible && opts !== null} options={opts} />

      {/* Global dialogs */}
      <ShouldConnectEmailDialog />
      <CampaignPhoneReachOutDialog />
      <CandidateOutreachDialog />

      {/* Global modals */}
      <EnrichVacancyModal />
      <CandidateFeedbackModal />
      <DiscardChangesModal />
      <SelectAtsVacancyForCampaignModal />
      <ReachoutInterviewModal />
      <NylasCalendarConnectionModal />
      <RejectCandidateModal />
      <RjCommBulkModal />
    </DialogContext.Provider>
  );
};
